import { Auth, CognitoUser } from '@aws-amplify/auth';
import { useContext, createContext, useEffect, useState, useMemo } from 'react';
import { AnalyticsEventType } from '../hooks/analytics/analytics.types';
import useAnalytics from '../hooks/analytics/useAnalytics';
import { Logger } from '../utils/logger';
import { useConfigContext } from './ConfigContext';

const logger = new Logger('AuthContext');

 type AuthContextProviderProps = {
   children: React.ReactNode;
 };

 type SignInProps = {
   username: string;
   password: string;
 };

 type AuthContextProps = {
   user: CognitoUser | null;
 };

const defaultAuthContext = { user: null };

const AuthContext = createContext<any>(defaultAuthContext);

function AuthContextProvider({ children }: AuthContextProviderProps): JSX.Element {
  const { id: playerId } = useConfigContext();
  const { trackAnalyticsEvent } = useAnalytics();
  const [user, setUser] = useState<CognitoUser | null>(null);

  const auth = useMemo(() => {
    Auth.configure({
      identityPoolId: import.meta.env.VITE_AWS_IDENTITY_POOL_ID, // where does this come from?
      region: import.meta.env.VITE_AWS_REGION,
      userPoolWebClientId: import.meta.env.VITE_AWS_POOL_WEBCLIENT_ID,
      userPoolId: import.meta.env.VITE_AWS_POOL_ID,
    });

    return Auth;
  }, []);

  const signInIfAuthorized = (userToAuthorize: CognitoUser) => {
    const userGroups = userToAuthorize.getSignInUserSession()?.getAccessToken().payload['cognito:groups'];
    logger.trace('check if user is authorized by user groups:', userGroups);

    if (userGroups?.includes(playerId) || userGroups?.includes('hdis')) {
      logger.debug('user signed in:', userToAuthorize);
      trackAnalyticsEvent({ type: AnalyticsEventType.USER_LOGIN, username: userToAuthorize.getUsername() });
      setUser(userToAuthorize);
    } else {
      logger.warn('user is not authorized:', userToAuthorize);
    }
  };

  const checkIfCurrentUserIsAuthenticated = async () => {
    try {
      const currentAuthenticatedUser = await auth.currentAuthenticatedUser();
      logger.debug('found already authenticated user:', currentAuthenticatedUser);
      signInIfAuthorized(currentAuthenticatedUser);
    } catch (error) {
      logger.warn('authentication of current user failed:', error);
    }
  };

  const signIn = async ({ username, password }: SignInProps) => {
    try {
      await auth.signIn(username, password).then((pendingUser) => {
        signInIfAuthorized(pendingUser);
      });
    } catch (error) {
      logger.error('error signing in:', error);
    }
  };

  const signOut = async () => {
    if (!user) return;
    try {
      await auth.signOut();
      logger.debug('user signed out:', user);
      trackAnalyticsEvent({ type: AnalyticsEventType.USER_LOGOUT, username: user.getUsername() });
      setUser(null);
    } catch (error) {
      logger.error('error signing out:', error);
    }
  };

  useEffect(() => {
    checkIfCurrentUserIsAuthenticated();
  }, []);

  return (
    <AuthContext.Provider value={{ user, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

type UseAuthContextProps = {
  signIn: ({ username, password }: SignInProps) => void;
  signOut: () => void;
} & AuthContextProps;

function useAuthContext(): UseAuthContextProps {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
}

export { AuthContextProvider, useAuthContext };
